import React from "react";
import InfoSection from "./InfoSection";
import TemplateSearch from "./TemplateSearch";
import HeaderActions from "./HeaderActions";
import CheckboxWithLabel from "./CheckboxWithLabel";
import DataExport from "./DataExport";
import DatezonePicker from "./DatazonePicker";
import { FaRegCalendarAlt, FaChevronDown } from "react-icons/fa";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

const StickyHeader = ({
  infoData,
  onInfoChange,
  templates,
  onSelectTemplate,
  buttons,
  saveTemplateConfig,
  leftLabel,
  showStatusBlink,
  mode = "templateSearch",
  modes = [],
  selectedMode,
  onModeChange,
  data = [],
  selectedValue = "",
  handleExportData = () => {},
  viewDropdown = null,
  onCalendarNav = null,
}) => {
  return (
    <div className="sticky w-full bg-white dark:bg-acs-dark-surface shadow-md p-fluid-2 transition-none">
      {/* 2-Column Layout */}
      <div className="flex justify-between flex-wrap gap-fluid-4">
        {/* Left Column */}
        <div className={`flex flex-col gap-fluid-1 flex-1 min-w-[250px] ${mode === "calendarSettings" ? "max-w-[600px]" : ""}`}>
          {/* Row 1: Date + nav buttons OR InfoSection */}
          {mode === "calendarSettings" ? (
            <div className="flex flex-col gap-fluid-2">
              {/* Date & Navigation */}
              <div className="flex items-center gap-fluid-2 flex-wrap">
                <DatezonePicker
                  date={infoData?.drop?.date || new Date().toISOString().slice(0, 10)}
                  timezone={infoData?.drop?.timezone || "PST"}
                  onDateChange={(date) => onInfoChange("drop", { date })}
                  onTimezoneChange={(timezone) => onInfoChange("drop", { timezone })}
                />

                {typeof onCalendarNav === "function" && (
                  <div className="flex items-center gap-fluid-1">
                    <button
                      onClick={() => onCalendarNav("today")}
                      className="flex items-center gap-1 px-fluid-button-x py-fluid-button-y rounded-full 
                                 border border-gray-300 dark:border-acs-dark-border 
                                 text-gray-700 dark:text-acs-dark-text bg-[#FAFAFB] dark:bg-acs-dark-surface 
                                 hover:bg-gray-100 dark:hover:bg-acs-dark-hover 
                                 text-fluid-sm transition-none"
                    >
                      <CalendarDays className="w-fluid-icon-sm h-fluid-icon-sm" />
                      Today
                    </button>

                    <button
                      onClick={() => onCalendarNav("prev")}
                      className="w-8 h-8 flex items-center justify-center rounded-full 
                                 border border-gray-300 dark:border-acs-dark-border 
                                 text-gray-700 dark:text-acs-dark-text 
                                 hover:bg-gray-100 dark:hover:bg-acs-dark-hover transition-none"
                    >
                      <ChevronLeft className="w-fluid-icon-xs h-fluid-icon-xs" />
                    </button>

                    <button
                      onClick={() => onCalendarNav("next")}
                      className="w-8 h-8 flex items-center justify-center rounded-full 
                                 border border-gray-300 dark:border-acs-dark-border 
                                 text-gray-700 dark:text-acs-dark-text 
                                 hover:bg-gray-100 dark:hover:bg-acs-dark-hover transition-none"
                    >
                      <ChevronRight className="w-fluid-icon-xs h-fluid-icon-xs" />
                    </button>
                  </div>
                )}
              </div>

              {/* Row 2: InfoSection with dropdowns (excluding drop field) */}
              <InfoSection
                data={Object.fromEntries(
                  Object.entries(infoData).filter(([key]) => key !== 'drop')
                )}
                onChange={onInfoChange}
                showStatusBlink={showStatusBlink}
                icons={{
                  calendar: <FaRegCalendarAlt className="w-fluid-icon-sm h-fluid-icon-sm" />,
                  dropdown: <FaChevronDown className="w-fluid-icon-xs h-fluid-icon-xs" />,
                }}
              />
            </div>
          ) : (
            <InfoSection
              data={infoData}
              onChange={onInfoChange}
              showStatusBlink={showStatusBlink}
              icons={{
                calendar: <FaRegCalendarAlt className="w-fluid-icon-sm h-fluid-icon-sm" />,
                dropdown: <FaChevronDown className="w-fluid-icon-xs h-fluid-icon-xs" />,
              }}
            />
          )}

          {/* Row 3: TemplateSearch or DataExport */}
          {mode === "dataExport" && <DataExport onExport={handleExportData} />}

          {mode === "templateSearch" && (
            <TemplateSearch
              modes={modes}
              selectedMode={selectedMode}
              onModeChange={onModeChange}
              data={data}
              onSelectItem={onSelectTemplate}
              selectedValue={selectedValue}
            />
          )}
        </div>

        {/* Right Column: Actions */}
        <div className="flex flex-col items-end justify-start mt-fluid-1 flex-1 min-w-[280px] max-w-[300px]">
          <div className="flex items-center gap-fluid-2 w-full justify-end">
            {/* ViewDropdown next to buttons */}
            {viewDropdown && (
              <div className="w-[clamp(7rem,10vw,9rem)]">
                {viewDropdown}
              </div>
            )}
            <HeaderActions buttons={buttons} className="text-fluid-xs" />
          </div>

          {saveTemplateConfig?.type === "checkbox" && (
            <div className="mt-fluid-2">
              <CheckboxWithLabel
                label="Save as template"
                checked={saveTemplateConfig.checked}
                onChange={saveTemplateConfig.onChange}
                labelClassName="text-fluid-sm dark:text-acs-dark-text"
              />
            </div>
          )}

          {saveTemplateConfig?.type === "button" && (() => {
            let label = "";
            return label ? (
              <button
                className="mt-fluid-2 bg-[#223688] text-white text-fluid-sm font-semibold 
                           px-4 py-2 rounded-md hover:bg-[#1A2B6D] 
                           dark:bg-[#223688] dark:text-white dark:hover:bg-[#1A2B6D] 
                           transition-none"
                onClick={saveTemplateConfig.onClick}
              >
                {label}
              </button>
            ) : null;
          })()}
        </div>
      </div>
    </div>
  );
};

export default StickyHeader;